<!--

Phaser and the game are quite large, and we don't want to block initial page rendering by them.
GameLoader is responsible for loading a separate game bundle, and showing a loading state in the meantime.

-->
<script lang="ts">
    import type {Scene} from "phaser";

    import {onMount} from "svelte";
    import Phaser from "phaser";
    import * as obstacleService from "@services/obstacleService";
    import {get} from "svelte/store";
    import {grid as gridStore} from "@src/appStore";

    import {staticRoot, currentLevel, metaContext, scene} from "@src/appStore";
    import {AlignGrid} from "@services/alignmentService";


    let phaserCtx: Scene;
    let headerHeight = 79;
    let footerHeight = 34;
    let gameHeight = (window.innerHeight * window.devicePixelRatio) - ((headerHeight + footerHeight) * window.devicePixelRatio);
    let gameWidth = window.innerWidth * window.devicePixelRatio;

    let grid: AlignGrid;
    let sloth: Phaser.GameObjects.Image | undefined;
    let currentPosition = 0;

    let levelIndicatorText: Phaser.GameObjects.Text | undefined;

    onMount(initialize);


    function initializeGame() {

        let config = {
            type: Phaser.AUTO,
            width: gameWidth,
            height: gameHeight,
            parent: "phaser-game-container",
            physics: {
                default: 'arcade',
                arcade: {
                    debug: true, // Set to false in production
                    gravity: { x: 0, y:0 } // Adjust gravity as needed
                }
            },
            scale: {
                mode: Phaser.Scale.FIT, // Ensures game is resized to fit screen
                autoCenter: Phaser.Scale.CENTER_BOTH // Center the game on screen
            },
            scene: {
                preload,
                create,
                update
            },
            pixelArt: true
        };

        function addTrees() {
            let t1 = $scene.add.image(gameWidth / 2, 0, "tree1");
            t1.setOrigin(.5, 0);
            t1.setDisplaySize(128, 128);

        }

        function addSloth() {
            sloth = $scene?.physics.add.image(gameWidth / 2, 0, "sloth");
            sloth.setDisplaySize(128, 128);
            sloth.setDepth(5);
            $scene.cameras.main.startFollow(sloth);

            grid.placeAt(2, currentPosition, sloth);
            return sloth;
        }



        function updateLevel() {
            currentLevel.set($currentLevel+1);
            if (levelIndicatorText) {
                levelIndicatorText.setText($currentLevel.toString());
            } else {
                levelIndicatorText = phaserCtx.add.text(0, 0, $currentLevel.toString(), {
                    fontSize: 128,
                    color: 'red',
                    depth: 2
                });
                levelIndicatorText.setScrollFactor(0);
            }
            grid.placeAt(0, 0, levelIndicatorText);
        }

        function initializeMovementListeners() {
            // Define the function to trigger on input
            const handleInput = () => {

                // Move character
                currentPosition--;
                updateLevel();
                grid.placeAt(2, currentPosition, sloth, true);

                // Redraw grid
                grid.showGrid($currentLevel);

            };

            // 1. Handle spacebar input
            phaserCtx.input?.keyboard!.on('keydown-SPACE', handleInput);

            // 2. Handle touch/click input (pointerdown works for both touch and mouse)
            phaserCtx.input.on('pointerdown', handleInput);
        }

        function preload(this: Scene) {
            console.log("preload game", $staticRoot);
            phaserCtx = this;
            scene.set(this);
            this.load.image('background', `${$staticRoot}/images/background-gradient.svg`);
            this.load.image('sloth', `${$staticRoot}/images/super-sloth-icon.png`);
            this.load.image("eagle", `${$staticRoot}/images/game/eagle.png`);
            this.load.image('tree1', `${$staticRoot}/images/game/tree/tree-1.png`);
            this.load.image('tree2', `${$staticRoot}/images/game/tree/tree-2.png`);
        }

        function initializeGrid(): AlignGrid {
            grid = new AlignGrid({
                scene: $scene.scene.scene,
                game: phaserCtx.game,
                rows: 9,
                cols: 5,
                height: (165.33333 * 9),
            });
            gridStore.set(grid);
            if ($metaContext.env === "dev")
                grid.showGrid($currentLevel);
            return grid;
        }

        function create() {

            // Create the background
            this.add.image(0, 0, "background")
                .setOrigin(0, 0)
                .setScrollFactor(0)
                .setDisplaySize(this.scale.width, this.scale.height);

            initializeGrid();
            // addTrees();

            addSloth();

            // Create the initial set of obstacles
            obstacleService.initialize(sloth);
            // addEagle();

            // Event listeners
            initializeMovementListeners();


        }

        function update() {
        }

        let game = new Phaser.Game(config);
    }


    function initialize() {
        metaContext.set(window.metaContext);
        initializeGame();
    }
</script>


<div id="phaser-game-container"></div>


<style lang="scss">
  @use "src/scss/colors";
</style>